import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'
import { NoteForm } from '@components/note'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'

const NOTE_TYPES = {
  EXTERNAL: 'external',
  INTERNAL: 'internal',
  CLIENT: 'client_note'
}

const NOTE_CONFIG = {
  [NOTE_TYPES.EXTERNAL]: {
    label: 'External Note',
    maxLength: 1000,
    getLabel: () => 'Notes Printed on Invoice'
  },
  [NOTE_TYPES.INTERNAL]: {
    label: 'Internal Note',
    maxLength: 500,
    getLabel: () => 'Notes for Technician'
  },
  [NOTE_TYPES.CLIENT]: {
    label: 'Client Note',
    maxLength: 1000,
    getLabel: (clientRebate) => (clientRebate > 0 ? `Client Rebate: ${clientRebate}%` : '')
  }
}

export const Note = ({
  submit,
  existingInternalNote,
  existingExternalNote,
  existingClientNote,
  allowEdit: allowEditProp = true,
  clientRebate
}) => {
  // A client user reads the External Note without adding; the Internal and Client Notes are
  // staff-only and the server does not send them to a client (CAR-3970).
  const isReadOnly = useIsReadOnly()
  const allowEdit = allowEditProp && !isReadOnly
  const [noteType, setNoteType] = useState(NOTE_TYPES.EXTERNAL)
  const [isAddNote, setIsAddNote] = useState(false)

  const currentNote = useMemo(() => {
    switch (noteType) {
      case NOTE_TYPES.INTERNAL:
        return existingInternalNote
      case NOTE_TYPES.CLIENT:
        return existingClientNote
      default:
        return existingExternalNote
    }
  }, [noteType, existingExternalNote, existingInternalNote, existingClientNote])

  const currentConfig = NOTE_CONFIG[noteType]
  const maxLength = currentConfig.maxLength

  const tabs = Object.entries(NOTE_CONFIG)
    .filter(([value]) => !isReadOnly || value === NOTE_TYPES.EXTERNAL)
    .map(([value, { label }]) => ({
      value,
      label
    }))

  const getNoteLabel = (noteType, clientRebate) => {
    switch (noteType) {
      case 'external':
        return 'Notes Printed on Invoice'
      case 'internal':
        return 'Notes for Technician'
      case 'client_note':
        return clientRebate > 0 ? `Client Rebate: ${clientRebate}%` : 'Client Note'
      default:
        return ''
    }
  }
  const label = getNoteLabel(noteType, clientRebate)

  const handleSubmit = useCallback(
    (value) => {
      const noteData = {
        notes_external: noteType === NOTE_TYPES.EXTERNAL ? value : existingExternalNote,
        notes_internal: noteType === NOTE_TYPES.INTERNAL ? value : existingInternalNote,
        client_notes: noteType === NOTE_TYPES.CLIENT ? value : existingClientNote
      }

      submit({ note: noteData, noteType })
      setIsAddNote(false)
    },
    [noteType, existingExternalNote, existingInternalNote, existingClientNote, submit]
  )

  const handleTabChange = useCallback((value) => {
    setNoteType(value)
    setIsAddNote(false)
  }, [])

  const showEmptyState = !currentNote && !isAddNote

  return (
    <AdasBox className='mb-5 mt-4'>
      {/* Tabs strip */}
      <AdasBox className='flex items-center gap-6 border-b border-[#EBEDF0]'>
        {tabs.map((tab) => {
          const active = tab.value === noteType
          return (
            <button
              key={tab.value}
              type='button'
              onClick={() => handleTabChange(tab.value)}
              className={`relative -mb-px py-2.5 text-[13px] font-bold uppercase tracking-wide transition-colors ${
                active ? 'text-[#0A1628]' : 'text-[#6B7280] hover:text-[#475569]'
              }`}
            >
              {tab.label}
              {active && <span className='absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-[#0A1628]' />}
            </button>
          )
        })}
      </AdasBox>

      {/* Body */}
      <AdasBox className='mt-4 rounded-lg bg-[#E8EFFF] px-4 py-8'>
        {showEmptyState ? (
          <AdasBox className='flex flex-col items-center justify-center'>
            <AdasTypography className='mb-4 text-[14px] text-[#475569]'>No Note Added</AdasTypography>
            <button
              type='button'
              onClick={() => setIsAddNote(true)}
              disabled={!allowEdit}
              aria-label='add note'
              className='inline-flex items-center gap-1 rounded-lg border border-[#0A1628] bg-white px-5 py-2 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
            >
              + Add Note
            </button>
          </AdasBox>
        ) : (
          <NoteForm
            noteType={noteType}
            existingNote={currentNote}
            cancel={() => setIsAddNote(false)}
            submit={handleSubmit}
            label={label}
            allowEdit={allowEdit}
            maxLength={maxLength}
            txAreaBgColor='white'
          />
        )}
      </AdasBox>
    </AdasBox>
  )
}

Note.propTypes = {
  submit: PropTypes.func.isRequired,
  existingInternalNote: PropTypes.string,
  existingExternalNote: PropTypes.string,
  existingClientNote: PropTypes.string,
  allowEdit: PropTypes.bool,
  noBottomBorder: PropTypes.bool,
  clientRebate: PropTypes.number
}
